import {
  autoUpdate,
  flip,
  type Placement,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import React from 'react';

export const useFloatingList = (
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  animationFrame = false,
  maxHeight?: (availableHeight: number) => string,
  placement?: Placement,
  shouldFlip = true,
) => {
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);

  const listRef = React.useRef<Array<HTMLElement | null>>([]);
  const virtualItemRef = React.useRef<HTMLElement | null>(null);

  const { x, y, strategy, refs, context } = useFloating<HTMLInputElement>({
    placement: placement ?? 'bottom',
    whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, { animationFrame }),
    open,
    onOpenChange: setOpen,
    middleware: [
      shouldFlip && flip(),
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: maxHeight ? maxHeight(availableHeight) : `${Math.min(availableHeight, 260)}px`,
          });
        },
        padding: 16,
      }),
    ],
  });

  const role = useRole(context, { role: 'listbox' });
  const dismiss = useDismiss(context);
  const listNav = useListNavigation(context, {
    listRef,
    virtualItemRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([role, dismiss, listNav]);

  return {
    x,
    y,
    strategy,
    refs,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    setActiveIndex,
    activeIndex,
    listRef,
  };
};
